import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ShieldCheck, RefreshCw, Copy, Check } from "lucide-react";

/**
 * EmailSettings — admin control for the sender domain used by the digest.
 * Uses Resend's Domain API to add a domain, then displays the DNS records the
 * admin needs to paste into their registrar. Once DNS is verified, admin can
 * update sender + recipient to any address on that domain.
 */
export default function EmailSettings() {
  const [state, setState] = useState(null);
  const [domain, setDomain] = useState("");
  const [sender, setSender] = useState("");
  const [recipient, setRecipient] = useState("");
  const [schedule, setSchedule] = useState("daily");
  const [busy, setBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-settings");
      setState(data);
      setSender(data.sender || "");
      setRecipient(data.recipient || "");
      setSchedule(data.digest_schedule || "daily");
    } catch (err) {
      console.error("Load email settings failed", err);
    }
  };

  useEffect(() => { load(); }, []);

  const addDomain = async () => {
    if (!domain.trim()) { toast.error("Enter a domain name"); return; }
    setBusy(true);
    try {
      await api.post("/admin/domains", { domain: domain.trim() });
      toast.success("Domain added. Now paste the DNS records into your registrar.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/domains/verify");
      toast.success(`Status: ${data.status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/email-settings", { sender, recipient, digest_schedule: schedule });
      toast.success("Digest settings updated.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyRecord = (val, idx) => {
    navigator.clipboard.writeText(val);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1200);
  };

  if (!state) return null;

  const statusColor =
    state.domain_status === "verified" ? "text-[#E6B860]" :
    state.domain_status === "pending" || state.domain_status === "not_started" ? "text-[#A8A095]" :
    "text-[#8C3A34]";

  return (
    <div className="border border-[rgba(217,180,114,0.2)] rounded-sm p-5 mb-6" data-testid="email-settings">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-4 h-4 text-[#D9B472]" />
        <span className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass">Digest email settings</span>
      </div>

      {!state.domain_id ? (
        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Add a sender domain</label>
          <p className="text-xs text-[#A8A095] font-serif-body mb-2 italic">Right now digests can only go to your verified Resend inbox. Add and verify a domain to route them anywhere.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="quill.ink"
              className="flex-1 bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-3 py-2 text-[#F7F3EC] font-mono-brass text-sm"
              data-testid="domain-input"
            />
            <button onClick={addDomain} disabled={busy} className="btn-buy px-4 py-2 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest font-semibold disabled:opacity-50" data-testid="domain-add">
              {busy ? "Adding…" : "Add domain"}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Domain</div>
              <div className="font-mono-brass text-[#F7F3EC]" data-testid="domain-name">{state.domain_name}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Status</div>
              <div className={`font-mono-brass uppercase tracking-widest text-xs ${statusColor}`} data-testid="domain-status">{state.domain_status || "unknown"}</div>
            </div>
            <button onClick={verify} disabled={busy} className="btn-ghost-gold px-3 py-1.5 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="domain-verify">
              <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Re-check
            </button>
          </div>

          {state.domain_records?.length > 0 && (
            <div className="mt-3 border border-[rgba(217,180,114,0.1)] rounded-sm p-3 bg-[#0D0C0A]" data-testid="dns-records">
              <div className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass mb-2">DNS records to paste into your registrar</div>
              <div className="space-y-2">
                {state.domain_records.map((r, i) => (
                  <div key={`${r.type}-${r.name}-${i}`} className="grid grid-cols-[auto_auto_1fr_auto] gap-3 items-center text-xs font-mono-brass">
                    <span className="text-[#D9B472] uppercase tracking-widest">{r.type}</span>
                    <span className="text-[#A8A095] truncate max-w-[180px]" title={r.name}>{r.name}</span>
                    <span className="text-[#F7F3EC] truncate" title={r.value}>{r.value}</span>
                    <button type="button" onClick={() => copyRecord(r.value, i)} className="text-[#D9B472] hover:text-[#E6B860]" title="Copy value">
                      {copiedIdx === i ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#6E675E] font-serif-body italic mt-3">Once these records are live and DNS has propagated, click Re-check. When status flips to <span className="text-[#E6B860]">verified</span>, you can send from any address on this domain.</p>
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Sender (from)</label>
          <input
            type="email"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-3 py-2 text-[#F7F3EC] font-mono-brass text-sm"
            data-testid="sender-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Digest recipient</label>
          <input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-3 py-2 text-[#F7F3EC] font-mono-brass text-sm"
            data-testid="recipient-input"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Cadence</label>
          <select
            value={schedule}
            onChange={(e) => setSchedule(e.target.value)}
            className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-3 py-2 text-[#F7F3EC] font-mono-brass text-sm"
            data-testid="schedule-select"
          >
            <option value="daily">Daily · every 24h</option>
            <option value="weekly">Weekly · every 7 days</option>
            <option value="off">Off · manual only</option>
          </select>
        </div>
      </div>
      {state.digest_last_sent_at && (
        <div className="text-[10px] text-[#6E675E] font-mono-brass mt-2" data-testid="digest-last-sent">Last digest sent · {new Date(state.digest_last_sent_at).toLocaleString()}</div>
      )}
      <button onClick={save} disabled={busy} className="btn-buy mt-3 px-4 py-2 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest font-semibold disabled:opacity-50" data-testid="email-settings-save">
        Save settings
      </button>
    </div>
  );
}
